import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { CourseContext } from '../contexts/CourseContext';
import '../styles/RemediationReportPage.css';

export default function RemediationReportPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCourseById, isAuthorizedCourse } = useContext(CourseContext);
  const searchParams = new URLSearchParams(location.search);
  
  const groupId = searchParams.get('groupId') || location.state?.selectedGroup;
  const selectedGroupName = searchParams.get('groupName') || location.state?.selectedGroupName || 'Group';
  
  // Verify this is an authorized course
  const courseData = getCourseById(courseId);
  const courseName = courseData?.fullname || searchParams.get('courseName') || location.state?.courseName || 'Course';
  
  if (!isAuthorizedCourse(courseId)) {
    console.warn(`[REMEDIATION-REPORT] Unauthorized course access attempt: ${courseId}`);
  }
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const sealImageSrc = `${import.meta.env.BASE_URL}JCAC-Seal_Transparent-version(small).png`;

  useEffect(() => {
    // If no group is selected, redirect to course selection
    if (!groupId) {
      console.warn(`[REMEDIATION-REPORT] No group selected for course ${courseId}, redirecting to group selection`);
      navigate(`/course/${courseId}`, { replace: true });
      return;
    }
    
    loadReport();
  }, [courseId, groupId, location.search, navigate]);

  const loadReport = async () => {
    if (!groupId) {
      setError('No group selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await apiClient.get(`/api/reports/class/${courseId}`, {
        params: { groupId }
      });

      if (response.data.success || response.data.data) {
        setReport(response.data.data);
        console.log('Report loaded:', response.data.data);
      } else {
        setError('No report data received');
      }
    } catch (err) {
      console.error('Error loading report:', err);
      setError(err.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatAssessmentLabel = (value) => {
    const normalized = String(value || '').toLowerCase();
    const partMatch = normalized.match(/^part\s*([12])[-\s_]*(knowledge|performance)$/);
    if (partMatch) {
      const assessmentName = partMatch[2] === 'knowledge' ? 'Knowledge Test' : 'Performance Test';
      return `Part ${partMatch[1]} ${assessmentName}`;
    }

    if (normalized === 'knowledge') {
      return 'Knowledge Test';
    }

    if (normalized === 'performance') {
      return 'Performance Test';
    }

    return String(value || 'Assessment');
  };

  const getAssessmentSortOrder = (value) => {
    const normalized = String(value || '').toLowerCase();
    const orderedKeys = ['part1-knowledge', 'part1-performance', 'part2-knowledge', 'part2-performance', 'knowledge', 'performance'];
    const explicitIndex = orderedKeys.indexOf(normalized);
    return explicitIndex >= 0 ? explicitIndex : orderedKeys.length;
  };

  if (loading) {
    return <div className="remediation-loading">Loading remediation report...</div>;
  }

  if (error) {
    return (
      <div className="remediation-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="remediation-error">
        <h2>No Report Available</h2>
        <p>No remediation data was generated. Please ensure there are students with failing scores in the selected group.</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const failedStudents = report.students || [];
  const classNumberDisplay = String(selectedGroupName || '').trim() || String(groupId || 'N/A');
  const handleViewUnmappedItems = () => {
    if (!groupId) {
      return;
    }

    const searchParams = new URLSearchParams({
      view: 'unmapped',
      courseId: String(courseId),
      groupId: String(groupId),
      groupName: String(selectedGroupName || '')
    });

    navigate(`/admin?${searchParams.toString()}`);
  };

  return (
    <div className="remediation-report-container">
      {/* Non-print header with controls */}
      <div className="report-header-controls">
        <div className="header-nav-buttons">
          <button className="btn-dashboard" onClick={() => navigate('/dashboard')}>
            ← Dashboard
          </button>
          <button className="btn-back" onClick={() => navigate(-1)}>
            ← Back to Course
          </button>
        </div>
        <div className="header-buttons">
          {groupId && (
            <button className="btn-download" onClick={handleViewUnmappedItems}>
              Unmapped Incorrect Items (Needs Mapping)
            </button>
          )}
          <button className="btn-print" onClick={handlePrint}>
            🖨️ Print Report
          </button>
        </div>
      </div>

      {/* Printable report content */}
      <div className="remediation-report">
        {/* Header with Logo */}
        <div className="report-header">
          <div className="header-logo">
            <img 
              src={sealImageSrc}
              alt="JCAC Seal" 
              className="logo-image"
            />
          </div>
          <div className="header-title">
            <h1>Remediation Report</h1>
            <p className="report-course-name">{courseName}</p>
            <p className="report-date">Generated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        {/* Summary Section */}
        <div className="report-summary">
          <div className="summary-grid">
            <div className="summary-item">
              <span className="label">Class Number:</span>
              <span className="value">{classNumberDisplay}</span>
            </div>
            <div className="summary-item">
              <span className="label">Total Students:</span>
              <span className="value">{report.summary?.totalStudents || 0}</span>
            </div>
            <div className="summary-item">
              <span className="label">Students Needing Remediation:</span>
              <span className="value remediation-highlight">{report.summary?.failedStudents || failedStudents.length}</span>
            </div>
          </div>
        </div>

        {/* Students Remediation Details */}
        <div className="report-content">
          <h2>Student Remediation Details</h2>

          {failedStudents.length === 0 ? (
            <div className="no-failures">
              <p>No students require remediation. All students passed the assessment!</p>
            </div>
          ) : (
            <div className="students-list">
              {failedStudents.map((student) => (
                <div key={student.id} className="student-remediation">
                  <div className="student-print-header">
                    <img
                      src={sealImageSrc}
                      alt="JCAC Seal"
                      className="student-print-logo"
                    />
                    <div className="student-print-title-wrap">
                      <div className="student-print-title-row">
                        <p className="student-print-report-title">Remediation Report</p>
                        <p className="student-print-generated">Generated: {new Date().toLocaleDateString()}</p>
                      </div>
                      <p className="student-print-course-name">{courseName}</p>
                      <p className="student-print-course-name">{student.fullname}</p>
                    </div>
                  </div>

                  <div className="student-header">
                    <h3>{student.fullname}</h3>
                    <div className="student-meta">
                      <span className="score-badge">
                        Score: {student.score !== undefined ? student.score.toFixed(2) : 'N/A'}%
                      </span>
                      {Object.entries(student.assessmentScores || {})
                        .sort(([left], [right]) => getAssessmentSortOrder(left) - getAssessmentSortOrder(right))
                        .map(([assessmentKey, score]) => (
                          <span key={assessmentKey} className="score-badge">
                            {formatAssessmentLabel(assessmentKey)}: {Number(score).toFixed(2)}%
                          </span>
                        ))}
                    </div>
                  </div>

                  <div className="objectives-section">
                    <h4>Objectives Needing Remediation:</h4>
                    <div className="question-totals">
                      <span className="total-pill total-pill-incorrect">Incorrect: {student.questionTotals?.incorrect ?? 0}</span>
                      <span className="total-pill total-pill-correct">Correct: {student.questionTotals?.correct ?? 0}</span>
                      <span className="total-pill total-pill-total">Total: {student.questionTotals?.total ?? 0}</span>
                    </div>
                    {student.assessmentBreakdown && Object.keys(student.assessmentBreakdown).length > 0 ? (
                      <>
                        {Object.values(student.assessmentBreakdown)
                          .sort((left, right) => getAssessmentSortOrder(left.assessmentKey || left.assessmentType) - getAssessmentSortOrder(right.assessmentKey || right.assessmentType))
                          .map((assessment) => (
                          <div key={assessment.assessmentKey || assessment.assessmentType || assessment.quizId} style={{ marginTop: 12 }}>
                            <h5 style={{ margin: '0 0 6px' }}>{assessment.assessmentLabel || formatAssessmentLabel(assessment.assessmentKey || assessment.assessmentType)}</h5>
                            <div className="question-totals" style={{ marginBottom: 8 }}>
                              <span className="total-pill total-pill-incorrect">Incorrect: {assessment.questionTotals?.incorrect ?? 0}</span>
                              <span className="total-pill total-pill-correct">Correct: {assessment.questionTotals?.correct ?? 0}</span>
                              <span className="total-pill total-pill-total">Total: {assessment.questionTotals?.total ?? 0}</span>
                            </div>
                            {assessment.objectivePerformanceRows && assessment.objectivePerformanceRows.length > 0 ? (
                              <div className="objectives-table-wrap">
                                <table className="objectives-table">
                                  <thead>
                                    <tr>
                                      <th>Objective</th>
                                      <th>Missed</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {assessment.objectivePerformanceRows.map((row, idx) => (
                                      <tr key={`${assessment.assessmentKey || assessment.assessmentType || 'assessment'}-${row.objectiveId || idx}`}>
                                        <td>{row.objective}</td>
                                        <td>{row.missed}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="mastered-msg">All objectives mastered!</p>
                            )}
                          </div>
                        ))}
                      </>
                    ) : student.objectivePerformanceRows && student.objectivePerformanceRows.length > 0 ? (
                      <div className="objectives-table-wrap">
                        <table className="objectives-table">
                          <thead>
                            <tr>
                              <th>Objective</th>
                              <th>Missed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {student.objectivePerformanceRows.map((row, idx) => (
                              <tr key={row.objectiveId || idx}>
                                <td>{row.objective}</td>
                                <td>{row.missed}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : student.remediationObjectives && student.remediationObjectives.length > 0 ? (
                      <div className="objectives-table-wrap">
                        <table className="objectives-table">
                          <thead>
                            <tr>
                              <th>Objective</th>
                              <th>Missed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {student.remediationObjectives.map((row, idx) => (
                              <tr key={row.objectiveCode || idx}>
                                <td>{row.objectiveLabel || row.objectiveText || row.objectiveCode}</td>
                                <td>{row.missCount > 0 ? row.missCount : 'Fallback'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="mastered-msg">All objectives mastered!</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="report-footer">
          <p>This report contains remediation guidance based on student assessment performance.</p>
          <p className="footer-date">CyberTrek Learning Platform • {new Date().getFullYear()}</p>
        </div>
      </div>
    </div>
  );
}
